import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, TextField, ToggleButton, ToggleButtonGroup } from "@mui/material";
import MovieCreationOutlinedIcon from "@mui/icons-material/MovieCreationOutlined";
import QuizOutlinedIcon from "@mui/icons-material/QuizOutlined";
import { addNewGame, getGamesData } from "../redux/asyncThunk/userActions";
import { useNavigate } from "react-router-dom";
import { sanitizePlainText } from "../utils/inputValidation";
import { AUTH_FIELD_SX } from "../pages/AuthLayout";
import { CONTENT_TYPES } from "../utils/contentType";
import { userSlice } from "../redux/user/userSlice";
import { markTrainerProjectHint } from "../utils/trainerProjectHint";

const { selectProject } = userSlice;

export default function NewGame() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const games = useSelector((state) => state.user?.games);
  const user = useSelector((state) => state.user?.data?.user);

  const [textProject, setTextProject] = useState("");
  const [trainerTheme, setTrainerTheme] = useState("");
  const [contentType, setContentType] = useState(CONTENT_TYPES.scenario);

  const checkGamesTitle = useCallback(
    (count) => !games.find((el) => el.title === `Новый проект-${count}`),
    [games]
  );

  useEffect(() => {
    if (!games?.length) {
      setTextProject("Новый проект-1");
    } else {
      let count = 1;
      while (!checkGamesTitle(count)) {
        count++;
      }
      setTextProject(`Новый проект-${count}`);
    }
  }, [checkGamesTitle, games]);

  const isTrainer = contentType === CONTENT_TYPES.trainer;

  return (
    <div className="app-dialog__body">
      <div className="app-dialog__title">
        {isTrainer ? "Новый тренажёр" : "Создайте проект"}
      </div>

      <span className="app-dialog__label">Тип проекта</span>
      <ToggleButtonGroup
        exclusive
        fullWidth
        size="small"
        value={contentType}
        onChange={(_, value) => {
          if (value) setContentType(value);
        }}
        className="app-dialog__type-toggle"
      >
        <ToggleButton value={CONTENT_TYPES.scenario}>
          <MovieCreationOutlinedIcon fontSize="small" sx={{ mr: 0.75 }} />
          Сценарий
        </ToggleButton>
        <ToggleButton value={CONTENT_TYPES.trainer}>
          <QuizOutlinedIcon fontSize="small" sx={{ mr: 0.75 }} />
          Тренажёр
        </ToggleButton>
      </ToggleButtonGroup>

      <TextField
        label={isTrainer ? "Название тренажёра" : "Название проекта"}
        value={textProject}
        size="small"
        fullWidth
        sx={AUTH_FIELD_SX}
        onChange={(ev) =>
          setTextProject(sanitizePlainText(ev.target.value, 120))
        }
      />

      {isTrainer ? (
        <TextField
          label="Тема тренажёра"
          value={trainerTheme}
          size="small"
          fullWidth
          sx={{ ...AUTH_FIELD_SX, mt: 1.5 }}
          placeholder="Например: вопросы по математике"
          onChange={(ev) =>
            setTrainerTheme(sanitizePlainText(ev.target.value, 200))
          }
        />
      ) : null}

      <p className="app-dialog__hint">
        {isTrainer
          ? "После создания откроется форма: добавляйте вопросы, отмечайте правильные ответы и смотрите превью для мессенджера."
          : "Интерактивный сценарий с видео и ветвлением по выбору."}
      </p>

      <div className="app-dialog__actions">
        <Button
          variant="contained"
          className="app-btn app-btn--primary"
          disabled={isTrainer && !trainerTheme.trim()}
          onClick={() => {
            dispatch(
              addNewGame({
                userId: user.id,
                textProject,
                contentType,
                trainerTheme: isTrainer ? trainerTheme.trim() : "",
                onSuccess: (createdId) => {
                  dispatch(getGamesData({ id: user.id }));
                  if (createdId) {
                    if (isTrainer) {
                      markTrainerProjectHint(createdId);
                    }
                    dispatch(selectProject(createdId));
                    navigate(
                      isTrainer
                        ? `/main/${createdId}?type=trainer`
                        : `/main/${createdId}`,
                      { replace: true }
                    );
                  } else {
                    navigate("/main", { replace: true });
                  }
                },
              })
            );
          }}
        >
          Создать
        </Button>
      </div>
    </div>
  );
}
